import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { GenerationState } from "@/components/GenerationState";
import { GuideRateLimited } from "@/components/GuideRateLimited";
import { Header } from "@/components/Header";
import { Sidebar } from "@/components/Sidebar";
import { Stage } from "@/components/Stage";
import { ContentError, isRateLimited } from "@/content/loader";
import { productQuery, stepsQuery } from "@/content/queries";
import { stepStart } from "@/engine/playback";
import { fr } from "@/i18n/fr";
import { useViewerStore } from "@/state/store";

interface ProductProps {
	slug: string;
}

/**
 * A product (`/products/$slug`): its 3D assembly viewer once the guide is ready, its generation
 * state before.
 */
export default function Product({ slug }: ProductProps) {
	const product = useQuery(productQuery(slug));
	const detail = product.data;
	// cached presigned URLs may have expired: wait for the refetch that runs on mount
	const viewer =
		detail?.viewer && !(product.isStale && product.isFetching)
			? detail.viewer
			: null;
	const steps = useQuery({
		...stepsQuery(slug, viewer?.version ?? "", viewer?.steps_url ?? ""),
		enabled: viewer !== null,
	});
	// read at load time only: a re-signed URL of the same files must not reload the model
	const modelUrl = useRef<string | null>(null);
	modelUrl.current = viewer?.model_url ?? null;

	// push the product data into the viewer store
	useEffect(() => {
		useViewerStore.getState().startLoading(slug);
	}, [slug]);
	useEffect(() => {
		const store = useViewerStore.getState();
		// a refetch of the same version hands back the same cached steps: keep the current step
		if (steps.data && modelUrl.current && store.data !== steps.data) {
			store.setData(steps.data, modelUrl.current);
		}
	}, [steps.data]);
	useEffect(() => {
		if (detail)
			document.title = `${detail.name} – ${detail.designation} · ${fr.appTitle}`;
	}, [detail]);
	const missingFiles = detail?.generation_status === "ready" && !detail.viewer;
	// a refresh refused by the rate limit keeps the guide already on screen
	const rateLimited = isRateLimited(product.error) ? product.error : null;
	const error = (rateLimited ? null : product.error) ?? steps.error;
	useEffect(() => {
		if (error) useViewerStore.getState().setError(error.message);
		else if (missingFiles) useViewerStore.getState().setError(fr.missingFiles);
	}, [error, missingFiles]);

	// keyboard: ← → steps, space play / replay, R recentre
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const tag = (e.target as HTMLElement | null)?.tagName ?? "";
			if (["INPUT", "SELECT", "BUTTON", "TEXTAREA"].includes(tag)) return;
			const s = useViewerStore.getState();
			const step = s.data?.steps[s.stepIndex];
			if (!step) return;
			if (e.key === "ArrowRight") s.goStep(s.stepIndex + 1);
			else if (e.key === "ArrowLeft") s.goStep(s.stepIndex - 1);
			else if (e.key === " ") {
				if (!s.playing && s.frame >= step.f1) s.setFrame(stepStart(step));
				s.setPlaying(!s.playing);
			} else if (e.key === "r" || e.key === "R") s.requestRecenter();
			else return;
			e.preventDefault();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	// release focus after clicks so the space bar does not double-fire on a focused button
	useEffect(() => {
		const onClick = (e: MouseEvent) => {
			(e.target as HTMLElement | null)?.closest("button")?.blur();
		};
		window.addEventListener("click", onClick);
		return () => window.removeEventListener("click", onClick);
	}, []);

	if (product.error instanceof ContentError && product.error.status === 404)
		return (
			<output className="loading static">
				<div className="box">
					<b>{fr.productNotFound}</b>
					<Link to="/catalogue" className="pill mt-3 no-underline">
						← {fr.catalog}
					</Link>
				</div>
			</output>
		);
	if (!detail && rateLimited)
		return (
			<div className="app">
				<Header />
				<main className="relative col-span-2 min-h-0 flex-1">
					<GuideRateLimited
						since={product.errorUpdatedAt}
						retryAfterMs={rateLimited.retryAfterMs ?? 60_000}
						onRetry={() => product.refetch()}
					/>
				</main>
			</div>
		);
	// the 3D engine (WebGL) is only mounted for a product whose guide is ready
	if (!detail && !product.error)
		return (
			<div className="app">
				<Header />
				<main className="relative col-span-2 min-h-0 flex-1">
					<output className="loading">
						<div className="box">
							<b>{fr.loading}</b>
							{fr.loadingDetail}
						</div>
					</output>
				</main>
			</div>
		);
	if (detail && detail.generation_status !== "ready")
		return (
			<div className="app">
				<Header product={detail} />
				<main className="col-span-2 min-h-0 flex-1 overflow-y-auto">
					<GenerationState product={detail} />
				</main>
			</div>
		);
	return (
		<div className="app">
			<Header product={detail} />
			<Stage />
			<Sidebar />
		</div>
	);
}
